<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>Список пользователей</h2>
    </div>

    <!-- Поиск пользователей и кнопки на одном уровне -->
    <div class="mb-3">
      <div class="row align-items-center">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text">
              🔍
            </span>
            <input 
              type="text" 
              class="form-control" 
              placeholder="Поиск по имени, email, username или ID табеля..." 
              v-model="searchQuery"
              @input="onSearchInput"
            >
            <button 
              v-if="searchQuery" 
              class="btn btn-outline-secondary" 
              type="button" 
              @click="clearSearch"
            >
              ✕
            </button>
          </div>
        </div>
        <div class="col-md-6">
          <div class="d-flex gap-2 justify-content-end">
            <button class="btn btn-success" @click="createRegularUser">
              <i class="bi bi-person-plus"></i> Добавить пользователя
            </button>
            <button class="btn btn-primary" @click="createUser">
              <i class="bi bi-person-plus-fill"></i> Создать администратора
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Информация о пагинации -->
    <div v-if="pagination.total > 0" class="mb-3">
      <small class="text-muted">
        <span v-if="searchQuery">
          Найдено {{ pagination.total }} пользователей по запросу "{{ searchQuery }}"
          (показано {{ (pagination.page - 1) * pagination.size + 1 }}-{{ Math.min(pagination.page * pagination.size, pagination.total) }})
        </span>
        <span v-else>
          Показано {{ (pagination.page - 1) * pagination.size + 1 }}-{{ Math.min(pagination.page * pagination.size, pagination.total) }} 
          из {{ pagination.total }} пользователей
        </span>
      </small>
    </div>

    <div v-if="users.length > 0">
      <table class="table table-striped">
        <thead>
        <tr>
          <th>ID</th>
          <th>Имя</th>
          <th>Email</th>
          <th>Telegram ID</th>
          <th>Telegram Username</th>
          <th>ID табеля</th>
          <th>Есть доступ</th>
          <th>Безлимит</th>
          <th class="text-end">Месячный лимит</th>
          <th class="text-end">Расход за месяц, USD</th>
          <th class="text-end">Оставшиеся дни подписки</th>
          <th class="text-end">Действия</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="userItem in users" :key="userItem.id">
          <td>{{ userItem.id }}</td>
          <td>{{ userItem.name }}</td>
          <td>
            <span v-if="userItem.email">{{ userItem.email }}</span>
            <span v-else class="text-muted">—</span>
          </td>
          <td>{{ userItem.telegram_id || '—' }}</td>
          <td>{{ userItem.telegram_username || '—' }}</td>
          <td>{{ userItem.table_id || '—' }}</td>
          <td>{{ userItem.has_access ? "Есть" : "Нет" }}</td>
          <td>{{ userItem.has_limit ? "Нет" : "Да" }}</td>
          <td class="text-end">{{ userItem.monthly_limit }}</td>
          <td class="text-end">{{ userItem.monthly_cost ? userItem.monthly_cost.toFixed(2) : '0.00' }}</td>
          <td class="text-end">{{ userItem.subscription_days_left}}</td>
          <td class="text-end">
            <div class="d-flex justify-content-end gap-2">
              <router-link 
                :to="{ name: 'UserDetailsView', params: { id: userItem.id } }" 
                class="btn btn-info btn-sm" 
                title="Подробнее"
              >
                <i class="bi bi-eye"></i>
              </router-link>
              <button 
                v-if="!userItem.email"
                class="btn btn-warning btn-sm" 
                @click="grantAdminRights(userItem)"
                title="Предоставить права администратора"
              >
                <i class="bi bi-shield-plus"></i>
              </button>
              <button 
                v-if="userItem.email && !isCurrentUser(userItem)"
                class="btn btn-outline-warning btn-sm" 
                @click="revokeAdminRights(userItem)"
                title="Отозвать права администратора"
              >
                <i class="bi bi-shield-minus"></i>
              </button>
              <button 
                class="btn btn-secondary btn-sm" 
                @click="editUser(userItem)"
                title="Редактировать"
              >
                <i class="bi bi-pencil"></i>
              </button>
              <button 
                class="btn btn-danger btn-sm" 
                @click="confirmDelete(userItem)"
                title="Удалить"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>

      <!-- Пагинация -->
      <nav v-if="pagination.pages > 1" aria-label="Пагинация пользователей">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: pagination.page === 1 }">
            <button class="page-link" @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1">
              Предыдущая
            </button>
          </li>
          
          <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === pagination.page }">
            <button class="page-link" @click="changePage(page)">{{ page }}</button>
          </li>
          
          <li class="page-item" :class="{ disabled: pagination.page === pagination.pages }">
            <button class="page-link" @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.pages">
              Следующая
            </button>
          </li>
        </ul>
      </nav>

      <!-- Выбор размера страницы -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div>
          <label for="pageSize" class="form-label me-2">Пользователей на странице:</label>
          <select id="pageSize" class="form-select d-inline-block w-auto" v-model="pagination.size" @change="changePageSize">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <div v-else-if="!loading" class="text-center py-5">
      <p class="text-muted">Пользователи не найдены</p>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
    </div>

    <!-- Модалка для создания обычного пользователя -->
    <div class="modal fade" id="regularUserModal" tabindex="-1" aria-labelledby="regularUserModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Добавить пользователя</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitRegularUser">
              <div class="mb-3">
                <label for="regular_name" class="form-label">Имя <span class="text-danger">*</span></label>
                <input v-model="regularUserData.name" type="text" class="form-control" id="regular_name" required />
              </div>
              <div class="mb-3">
                <label for="regular_telegram_id" class="form-label">Telegram ID</label>
                <input v-model.number="regularUserData.telegram_id" type="number" class="form-control" id="regular_telegram_id" />
                <div class="form-text">Числовой ID пользователя в Telegram</div>
              </div>
              <div class="mb-3">
                <label for="regular_telegram_username" class="form-label">Telegram Username</label>
                <input v-model="regularUserData.telegram_username" type="text" class="form-control" id="regular_telegram_username" />
                <div class="form-text">Введите username без символа @</div>
              </div>
              <div class="alert alert-info" role="alert">
                <i class="bi bi-info-circle mr-1"></i>
                <strong> Важно:</strong> Укажите либо Telegram ID, либо Username (или оба поля).
              </div>
              <div class="mb-3">
                <label for="regular_monthly_limit" class="form-label">Месячный лимит <span class="italic">(Работает только если у пользователя включен лимит по токенам)</span></label>
                <input type="number" step="0.01" v-model="regularUserData.monthly_limit" class="form-control" id="regular_monthly_limit">
              </div>
              <div class="mb-3">
                <label for="regular_subscription_days" class="form-label">Количество оставшихся дней подписки</label>
                <input type="number" min="0" step="1" v-model="regularUserData.subscription_days_left" class="form-control" id="regular_subscription_days" @input="updateSubscriptionEndDate">
                <div v-if="regularUserData.subscription_days_left && regularUserData.subscription_days_left > 0" class="form-text">
                  Подписка до: <strong>{{ getSubscriptionEndDate(regularUserData.subscription_days_left) }}</strong>
                </div>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="regularUserData.has_limit" id="regular_has_limit">
                  <label class="form-check-label" for="regular_has_limit">
                    Включен лимит по токенам <span class="italic">(Если отключен, то пользователь будет иметь безлимит по токенам)</span>
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="regularUserData.has_access" id="regular_has_access">
                  <label class="form-check-label" for="regular_has_access">
                    Есть доступ <span class="italic">(Если отключен, то пользователь не сможет использовать бота)</span>
                  </label>
                </div>
              </div>
              <div class="d-flex">
                <button type="submit" class="btn btn-success ms-auto">Создать пользователя</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Модалка для создания администратора -->
    <div class="modal fade" id="userModal" tabindex="-1" aria-labelledby="userModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingUser ? 'Редактировать пользователя' : 'Создать администратора' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitUser">
              <div class="mb-3">
                <label for="name" class="form-label">Имя <span class="text-danger">*</span></label>
                <input v-model="userData.name" type="text" class="form-control" id="name" required />
              </div>
              <div class="mb-3">
                <label for="email" class="form-label">Email</label>
                <input v-model="userData.email" type="email" class="form-control" id="email" />
              </div>
              <div class="mb-3">
                <label for="telegram_id" class="form-label">Telegram ID</label>
                <input v-model.number="userData.telegram_id" type="number" class="form-control" id="telegram_id" />
                <div class="form-text">Числовой ID пользователя в Telegram</div>
              </div>
              <div class="mb-3">
                <label for="telegram_username" class="form-label">Telegram Username</label>
                <input v-model="userData.telegram_username" type="text" class="form-control" id="telegram_username" />
                <div class="form-text">Введите username без символа @</div>
              </div>
              <div class="alert alert-info" role="alert">
                <i class="bi bi-info-circle"></i>
                <strong> Важно:</strong> Укажите либо Telegram ID, либо Username (или оба поля).
              </div>
              <div class="mb-3">
                <label for="table_id" class="form-label">ID табеля</label>
                <input v-model="userData.table_id" type="text" class="form-control" id="table_id" />
              </div>
              <div class="mb-3">
                <label for="monthly_limit" class="form-label">Месячный лимит <span class="italic">(Работает только если у пользователя включен лимит по токенам)</span></label>
                <input type="number" step="0.01" v-model="userData.monthly_limit" class="form-control" id="monthly_limit">
              </div>
              <div class="mb-3">
                <label for="subscription_days" class="form-label">Количество оставшихся дней подписки</label>
                <input type="number" min="0" step="1" v-model="userData.subscription_days_left" class="form-control" id="subscription_days" @input="updateAdminSubscriptionEndDate">
                <div v-if="userData.subscription_days_left && userData.subscription_days_left > 0" class="form-text">
                  Подписка до: <strong>{{ getSubscriptionEndDate(userData.subscription_days_left) }}</strong>
                </div>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="userData.has_limit" id="has_limit">
                  <label class="form-check-label" for="has_limit">
                    Включен лимит по токенам <span class="italic">(Если отключен, то пользователь будет иметь безлимит по токенам)</span>
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="userData.has_access" id="has_access">
                  <label class="form-check-label" for="has_access">
                    Есть доступ <span class="italic">(Если отключен, то пользователь не сможет использовать бота)</span>
                  </label>
                </div>
              </div>
              <div class="d-flex">
                <button type="submit" class="btn btn-primary ms-auto">{{ editingUser ? 'Сохранить' : 'Создать администратора' }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Модалка для предоставления прав администратора -->
    <div class="modal fade" id="adminRightsModal" tabindex="-1" aria-labelledby="adminRightsModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Предоставить права администратора</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>Пользователь: <strong>{{ selectedUser?.name }}</strong></p>
            <form @submit.prevent="submitAdminRights">
              <div class="mb-3">
                <label for="admin_email" class="form-label">Email <span class="text-danger">*</span></label>
                <input v-model="adminRightsData.email" type="email" class="form-control" id="admin_email" required />
              </div>
              <div class="mb-3">
                <label for="admin_password" class="form-label">Пароль <span class="text-danger">*</span></label>
                <input v-model="adminRightsData.password" type="password" class="form-control" id="admin_password" required />
                <div class="form-text">Минимум 6 символов</div>
              </div>
              <div class="d-flex">
                <button type="submit" class="btn btn-warning ms-auto">Предоставить права</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Модалка для отзыва прав администратора -->
    <div class="modal fade" id="revokeAdminModal" tabindex="-1" aria-labelledby="revokeAdminModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Отозвать права администратора</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <i class="bi bi-exclamation-triangle"></i>
              <strong>Внимание!</strong> Вы собираетесь отозвать права администратора у пользователя.
            </div>
            <p>Пользователь: <strong>{{ selectedUser?.name }}</strong></p>
            <p>Email: <strong>{{ selectedUser?.email }}</strong></p>
            <p>После отзыва прав пользователь:</p>
            <ul>
              <li>Не сможет входить в админ-панель</li>
              <li>Потеряет доступ к управлению системой</li>
              <li>Сохранит доступ к Telegram боту (если включен)</li>
            </ul>
            <div class="alert alert-danger mt-3">
              <i class="bi bi-shield-exclamation"></i>
              <strong> Важно:</strong> Убедитесь, что в системе останется хотя бы один администратор!
            </div>
            <p><strong>Вы уверены, что хотите продолжить?</strong></p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Отмена</button>
            <button type="button" class="btn btn-danger" @click="submitRevokeAdminRights">
              <i class="bi bi-shield-minus"></i> Отозвать права
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Уведомления об ошибках или успешных действиях -->
    <div v-if="toastMessage" class="alert alert-info position-fixed bottom-0 end-0 p-3">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import axiosInstance from '../services/axiosInstance';

export default {
  data() {
    return {
      users: [],
      loading: false,
      currentUser: null,
      pagination: {
        page: 1,
        size: parseInt(localStorage.getItem('userListPageSize')) || 10,
        total: 0,
        pages: 0
      },
      userData: {
        name: '',
        email: '',
        telegram_id: null,
        telegram_username: '',
        table_id: '',
        monthly_cost: 0,
        monthly_limit: 0,
        has_access: true,
        has_limit: true,
        subscription_days_left: null,
      },
      regularUserData: {
        name: '',
        telegram_id: null,
        telegram_username: '',
        monthly_limit: 0,
        has_access: true,
        has_limit: true,
        subscription_days_left: null,
      },
      adminRightsData: {
        email: '',
        password: '',
      },
      editingUser: null,
      selectedUser: null,
      toastMessage: '',
      searchQuery: '',
      searchTimeout: null,
    };
  },
  computed: {
    visiblePages() {
      const current = this.pagination.page;
      const total = this.pagination.pages;
      const delta = 2; // Количество страниц слева и справа от текущей
      
      let start = Math.max(1, current - delta);
      let end = Math.min(total, current + delta);
      
      // Если мало страниц слева, добавляем справа
      if (current - delta <= 0) {
        end = Math.min(total, end + (delta - current + 1));
      }
      
      // Если мало страниц справа, добавляем слева
      if (current + delta >= total) {
        start = Math.max(1, start - (current + delta - total));
      }
      
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size
        };
        
        if (this.searchQuery && this.searchQuery.trim()) {
          params.search = this.searchQuery.trim();
        }
        
        const response = await axiosInstance.get('/users', { params });
        
        this.users = response.data.items;
        this.pagination.total = response.data.total;
        this.pagination.pages = response.data.pages;
      } catch (error) {
        this.showToast('Ошибка при загрузке пользователей');
        console.error('Error fetching users:', error);
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.pages && page !== this.pagination.page) {
        this.pagination.page = page;
        this.fetchUsers();
      }
    },
    changePageSize() {
      // Сохраняем размер страницы в localStorage
      localStorage.setItem('userListPageSize', this.pagination.size.toString());
      this.pagination.page = 1; // Сбрасываем на первую страницу при изменении размера
      this.fetchUsers();
    },
    createUser() {
      this.userData = {
        name: '',
        email: '',
        telegram_id: null,
        telegram_username: '',
        table_id: '',
        monthly_cost: 0,
        monthly_limit: 0,
        has_access: true,
        has_limit: true,
        subscription_days_left: null,
      };
      this.editingUser = null;
      const modal = new Modal(document.getElementById('userModal'));
      modal.show();
    },
    createRegularUser() {
      this.regularUserData = {
        name: '',
        telegram_id: null,
        telegram_username: '',
        monthly_limit: 0,
        has_access: true,
        has_limit: true,
        subscription_days_left: null,
      };
      const modal = new Modal(document.getElementById('regularUserModal'));
      modal.show();
    },
    editUser(userItem) {
      this.userData = { ...userItem };
      this.editingUser = userItem.id;
      const modal = new Modal(document.getElementById('userModal'));
      modal.show();
    },
    grantAdminRights(userItem) {
      this.selectedUser = userItem;
      this.adminRightsData = {
        email: '',
        password: '',
      };
      const modal = new Modal(document.getElementById('adminRightsModal'));
      modal.show();
    },
    revokeAdminRights(userItem) {
      this.selectedUser = userItem;
      const modal = new Modal(document.getElementById('revokeAdminModal'));
      modal.show();
    },
    async submitUser() {
      try {
        // Валидация telegram полей для администратора (если указаны)
        const hasTelegramId = this.userData.telegram_id && this.userData.telegram_id !== 0;
        const hasTelegramUsername = this.userData.telegram_username && this.userData.telegram_username.trim() !== '';
        
        if ((this.userData.telegram_id || this.userData.telegram_username) && !hasTelegramId && !hasTelegramUsername) {
          this.showToast('Укажите либо Telegram ID, либо Username');
          return;
        }

        // Подготавливаем данные для отправки, очищая пустые значения
        const dataToSend = { ...this.userData };
        if (!hasTelegramId) {
          dataToSend.telegram_id = null;
        }
        if (!hasTelegramUsername) {
          dataToSend.telegram_username = null;
        }

        if (this.editingUser) {
          await axiosInstance.put(`/users/${this.editingUser}`, dataToSend);
          this.showToast('Пользователь обновлен');
        } else {
          await axiosInstance.post('/users', dataToSend);
          this.showToast('Администратор создан');
        }
        await this.fetchUsers();
        const modal = Modal.getInstance(document.getElementById('userModal'));
        modal.hide();
      } catch (error) {
        if (error.response?.data?.detail) {
          this.showToast(`Ошибка: ${error.response.data.detail}`);
        } else {
          this.showToast('Ошибка при сохранении пользователя');
        }
        console.error('Error saving user:', error);
      }
    },
    async submitRegularUser() {
      try {
        // Валидация telegram полей для обычного пользователя
        const hasTelegramId = this.regularUserData.telegram_id && this.regularUserData.telegram_id !== 0;
        const hasTelegramUsername = this.regularUserData.telegram_username && this.regularUserData.telegram_username.trim() !== '';
        
        if (!hasTelegramId && !hasTelegramUsername) {
          this.showToast('Укажите либо Telegram ID, либо Username');
          return;
        }

        // Подготавливаем данные для отправки, очищая пустые значения
        const dataToSend = { ...this.regularUserData };
        if (!hasTelegramId) {
          dataToSend.telegram_id = null;
        }
        if (!hasTelegramUsername) {
          dataToSend.telegram_username = null;
        }

        await axiosInstance.post('/users/regular', dataToSend);
        this.showToast('Пользователь создан');
        await this.fetchUsers();
        const modal = Modal.getInstance(document.getElementById('regularUserModal'));
        modal.hide();
      } catch (error) {
        if (error.response?.data?.detail) {
          this.showToast(`Ошибка: ${error.response.data.detail}`);
        } else {
          this.showToast('Ошибка при создании пользователя');
        }
        console.error('Error creating regular user:', error);
      }
    },
    async submitAdminRights() {
      try {
        await axiosInstance.post(`/users/${this.selectedUser.id}/grant-admin`, this.adminRightsData);
        this.showToast('Права администратора предоставлены');
        await this.fetchUsers();
        const modal = Modal.getInstance(document.getElementById('adminRightsModal'));
        modal.hide();
      } catch (error) {
        if (error.response?.data?.detail) {
          this.showToast(`Ошибка: ${error.response.data.detail}`);
        } else {
          this.showToast('Ошибка при предоставлении прав администратора');
        }
        console.error('Error granting admin rights:', error);
      }
    },
    async submitRevokeAdminRights() {
      try {
        await axiosInstance.post(`/users/${this.selectedUser.id}/revoke-admin`);
        this.showToast('Права администратора отозваны');
        await this.fetchUsers();
        const modal = Modal.getInstance(document.getElementById('revokeAdminModal'));
        modal.hide();
      } catch (error) {
        if (error.response?.data?.detail) {
          this.showToast(`Ошибка: ${error.response.data.detail}`);
        } else {
          this.showToast('Ошибка при отзыве прав администратора');
        }
        console.error('Error revoking admin rights:', error);
      }
    },
    confirmDelete(userItem) {
      if (confirm(`Вы уверены, что хотите удалить пользователя "${userItem.name}"?`)) {
        this.deleteUser(userItem.id);
      }
    },
    async deleteUser(userId) {
      try {
        await axiosInstance.delete(`/users/${userId}`);
        this.showToast('Пользователь удален');
        
        // Если удалили последний элемент на странице и это не первая страница, переходим на предыдущую
        if (this.users.length === 1 && this.pagination.page > 1) {
          this.pagination.page--;
        }
        
        await this.fetchUsers();
      } catch (error) {
        this.showToast('Ошибка при удалении пользователя');
        console.error('Error deleting user:', error);
      }
    },
    showToast(message) {
      this.toastMessage = message;
      setTimeout(() => {
        this.toastMessage = '';
      }, 3000);
    },
    onSearchInput() {
      // Очищаем предыдущий таймер
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Устанавливаем новый таймер для debounce
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchUsers();
      }, 300); // Задержка 300ms
    },
    clearSearch() {
      this.searchQuery = '';
      this.pagination.page = 1;
      this.fetchUsers();
    },
    isCurrentUser(userItem) {
      // Проверяем, является ли пользователь текущим авторизованным пользователем
      return this.currentUser && this.currentUser.id === userItem.id;
    },
    async fetchCurrentUser() {
      try {
        const response = await axiosInstance.get('/users/current');
        this.currentUser = response.data;
      } catch (error) {
        console.error('Error fetching current user:', error);
        // Если не удалось получить данные о текущем пользователе, 
        // не показываем ошибку, просто не скрываем кнопки
      }
    },
    getSubscriptionEndDate(days) {
      if (!days || days <= 0) return '';
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + parseInt(days));
      return endDate.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    updateSubscriptionEndDate() {
      // Этот метод вызывается при изменении поля subscription_days_left
      // Vue автоматически обновит отображение даты через getSubscriptionEndDate
    },
    updateAdminSubscriptionEndDate() {
      // Этот метод вызывается при изменении поля subscription_days_left в форме администратора
      // Vue автоматически обновит отображение даты через getSubscriptionEndDate
    },
  },
  mounted() {
    this.fetchUsers();
    this.fetchCurrentUser();
  },
};
</script>

<style>
.table td {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alert {
  z-index: 1050;
}
.pagination .page-link {
  cursor: pointer;
}
.pagination .page-item.disabled .page-link {
  cursor: not-allowed;
}
</style>
